/* eslint-disable camelcase */
import { useState } from 'react'
import { camelCase, snakeCase } from 'lodash/fp'
import { Modal, ModalBody } from 'components/modal'
import {
  Message,
  MessageContents,
} from 'components/global-nav/components/MessageCenter/types'

// @ts-ignore-line
import { InfoButton } from 'components/buttons'
import MessageCard from 'components/global-nav/components/MessageCenter/MessageCard'

import css from './MessageCenterAdminTools.scss'

const HANDLEBARS_REGEX = /\[\[|\]\]|\(\(|\)\)|\[\[\[|\]\]\]|\{\{|\}\}|\{|\}/g

const ensureKeys = (
  object: { [key: string]: any },
  keys: string[]
): string[] => {
  const objectKeys = Object.keys(object)
  keys.forEach((key) => {
    if (!objectKeys.includes(key)) {
      objectKeys.push(key)
    }
  })
  return objectKeys
}

const serializeToJson = (contents: MessageContents) => {
  const snakeCasedObject = ensureKeys(contents, [
    'reportingTitle',
    'reportingCaption',
    'pushEvent',
  ])
    .sort()
    .reduce((acc, key) => {
      const snakeKey = snakeCase(key)
      if (key === 'thumbnailUrl') {
        if (contents[key as keyof MessageContents]?.trim() === '') {
          acc[snakeKey] = undefined
        } else {
          acc[snakeKey] = contents[key as keyof MessageContents] || undefined
        }
      } else if (key === 'reportingTitle') {
        acc[snakeKey] = contents.title.replace(HANDLEBARS_REGEX, '??')
      } else if (key === 'reportingCaption') {
        acc[snakeKey] = contents.caption.replace(HANDLEBARS_REGEX, '??')
      } else if (key === 'pushEvent') {
        acc[snakeKey] = 'Message Sent'
      } else {
        acc[snakeKey] = contents[key as keyof MessageContents]
      }

      return acc
    }, {} as Record<string, string | number | null | undefined>)

  return JSON.stringify(snakeCasedObject, null, 2)
}

const deserializeFromJson = (jsonString: string) => {
  const parsed = JSON.parse(jsonString)
  return Object.keys(parsed).reduce((acc, key) => {
    const camelKey = camelCase(key) as keyof MessageContents
    acc[camelKey] = parsed[key]
    return acc
  }, {} as MessageContents) as MessageContents
}

const MessageCenterAdminTools = () => {
  const defaultMessage: MessageContents = {
    caption: '...',
    reportingCaption: '...',
    messageType: '...',
    deeplinkUrl: 'https://www.babylist.com',
    templateName: '...',
    pushEvent: 'Message Sent',
    thumbnailUrl: undefined,
    title: '...',
    reportingTitle: '...',
    webUrl: 'https://www.babylist.com',
  }

  const [showModal, setShowModal] = useState(false)
  const [messageContents, setMessageContents] =
    useState<MessageContents>(defaultMessage)
  const [textAreaError, setTextAreaError] = useState('')
  const [jsonString, setJsonString] = useState(serializeToJson(defaultMessage))

  const handleTextAreaChange = (e: any) => {
    try {
      const processed = serializeToJson(deserializeFromJson(e.target.value))
      const message = deserializeFromJson(processed)
      setMessageContents(message)
      setJsonString(processed)
    } catch (error) {
      setTextAreaError('Invalid JSON')
    }
  }

  const handleFormChange = (e: any) => {
    setMessageContents({
      ...messageContents,
      [camelCase(e.target.name)]: e.target.value,
    })
    setJsonString(
      serializeToJson({
        ...messageContents,
        [camelCase(e.target.name)]: e.target.value,
      })
    )
  }

  return (
    <>
      <Modal
        show={showModal}
        title="Message Center Admin Tools"
        onHide={() => {
          setShowModal(false)
          setJsonString(serializeToJson(defaultMessage))
          setMessageContents(defaultMessage)
        }}
      >
        <ModalBody>
          <div className={css.PreviewWrapper}>
            <div className={css.FormWrapper}>
              <div>Builder</div>
              <br />
              {ensureKeys(messageContents, ['thumbnailUrl'])
                .sort()
                .map((key) => {
                  if (
                    key === 'reportingTitle' ||
                    key === 'reportingCaption' ||
                    key === 'test' ||
                    key === 'pushEvent'
                  )
                    return null
                  const snakeKey = snakeCase(key)
                  return (
                    <div>
                      <label htmlFor={`mc${snakeKey}`}>
                        {snakeKey}
                        <input
                          type="text"
                          value={
                            messageContents[key as keyof MessageContents] || ''
                          }
                          name={snakeKey}
                          onChange={handleFormChange}
                          className="form-control"
                        />
                      </label>
                    </div>
                  )
                })}
            </div>
            <div className={css.TextAreaWrapper}>
              <div className={css.MessagePreviewWrapper}>
                <div>Preview</div>
                <MessageCard
                  preview
                  message={
                    {
                      id: 1,
                      messageContents,
                      createdAt: new Date(),
                    } as Message
                  }
                  position={1}
                />
              </div>
              <div>{textAreaError}</div>
              <textarea
                onChange={(e) => {
                  handleTextAreaChange(e)
                }}
                value={jsonString}
              />
            </div>
          </div>
        </ModalBody>
      </Modal>
      <div className="h6 mtxl">Message Center Admin Tools</div>
      <div>
        <InfoButton
          block
          empty
          pill
          className="mbm"
          size="large"
          onClick={() => {
            setShowModal(true)
          }}
        >
          Builder & Preview
        </InfoButton>
      </div>
    </>
  )
}

export default MessageCenterAdminTools
